"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Heading from "@/component/ui/Heading";
import Container from "@/component/ui/CustomContainer";
import { VariantItem } from "@/app/utilities/interfaces";
import LeadButton from "@/component/ui/Lead/LeadButton";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";
import { getBrands } from "@/app/utilities/apis";
import type { Brand, Model, SelectedCar, Variant } from "@/app/utilities/compareTypes";
import { formatPrice, generateCompareURLByDetailedSlugs } from "@/app/utilities/helper";
import CarSelector from "@/component/ui/CarSelector";
import { findSpecValue } from "@/app/utilities/modelTagConverter";
import ImageWithFallback from "@/component/ui/ImageWithFallback";
import SectionDescription from "@/component/ui/SectionDescription/SectionDescription";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CarVariant {
    id: string;
    name: string;
    engine: string;
    transmission: string;
    fuel: string;
    price: string;
    image: string;
    mileage?: string;
}

interface Props {
    title: string;
    subtitle?: string;
    variants: VariantItem[];
    mileageType?: "mileage" | "range";
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_COMPARE = 4;
const DEFAULT_VISIBLE = 3;

// Override classes for the "Discontinued" button, translated from the old
// `.discVairentBtn` CSS Module class. It always wins over whatever base
// className is passed in (mirroring the source-order precedence it had over
// `.vtOfferBtn` / `.vtViewDetails` in the shared stylesheet).
const discVairentBtnClass =
    "!w-full !py-3 !px-2 !rounded-lg !font-semibold !text-sm !border !border-[#ef4444] !text-[#ef4444] !bg-[#fef2f2] !cursor-not-allowed hover:!bg-[#fef2f2] hover:!text-[#ef4444] hover:!border-[#ef4444] max-[767px]:!w-auto max-[767px]:!bg-transparent max-[767px]:!border-none max-[767px]:!rounded-none max-[767px]:!p-0 max-[767px]:!text-[#ef4444] max-[767px]:hover:!bg-transparent max-[767px]:hover:!text-[#ef4444] max-[767px]:hover:!border-none";

const toCompareKey = (variant: VariantItem) => {
    const detailedSlug = variant.modelDetailedSlug;
    const variantSlug = variant.slug;
    return variantSlug ? `${detailedSlug}+${variantSlug}` : detailedSlug;
};

const selectedCarToVariantItem = (car: SelectedCar): VariantItem => ({
    id: car._id,
    name: car.variant_name,
    slug: car.variant_slug,
    brandSlug: car.brand_slug,
    modelSlug: car.model_slug,
    modelName: car.model_name,
    modelId: car.model_id || "",
    modelDetailedSlug: car.detailed_slug || "",
    engine: (
        findSpecValue(car as any, "engine_displacement") ||
        findSpecValue(car as any, "displacement") ||
        findSpecValue(car as any, "max_power") ||
        ""
    ).trim(),
    transmission: ((car as any)?.transmission || findSpecValue(car as any, "transmission_type") || "").trim(),
    fuel: car?.fuel || '',
    mileage: (
        findSpecValue(car as any, "arai_mileage") ||
        findSpecValue(car as any, "mileage") ||
        findSpecValue(car as any, "range") ||
        ""
    ).trim(),
    price: car.price ? `${formatPrice(car.price)}` : "",
    isUpcoming: car.is_upcoming || false,
    isDiscontinued: car.is_discontinued,
    brandName: car.brand_name,
    is_sanity_done: true,
    battery_capacity: "",
    featured_image: car.featured_image,
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function RevampVariantsTable({ title, subtitle, variants, mileageType }: Props) {
    if (!variants.length) return null;

    const [selectedFuel, setSelectedFuel] = useState<string>("All");
    const [selectedTransmission, setSelectedTransmission] = useState<string>("All");
    const [compareList, setCompareList] = useState<VariantItem[]>([]);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [showCompareSelector, setShowCompareSelector] = useState(false);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [brandsFetched, setBrandsFetched] = useState(false);
    const [selectorModels, setSelectorModels] = useState<Model[]>([]);
    const [selectorVariants, setSelectorVariants] = useState<Variant[]>([]);

    const router = useRouter();

    useEffect(() => {
        if (!showCompareSelector || brandsFetched || brandsLoading) return;
        const fetchBrands = async () => {
            setBrandsLoading(true);
            try {
                const res = await getBrands({ page: 1, limit: 100, is_active: true, sort: "asc" });
                if (res?.status === "success") setBrands(res.data || []);
            } catch (e) {
                console.error(e);
            } finally {
                setBrandsLoading(false);
                setBrandsFetched(true);
            }
        };
        fetchBrands();
    }, [showCompareSelector, brandsFetched, brandsLoading]);

    useEffect(() => {
        const keys = compareList.map(toCompareKey).filter(Boolean);
        Cookies.set("selected_variant_slugs", JSON.stringify(keys), { path: "/" });
    }, [compareList]);

    // ── Filtering ──────────────────────────────────────────────────────────────

    const filtered = variants.filter((v) => {
        const fuelMatch = selectedFuel === "All" || v.fuel.toLowerCase() === selectedFuel.toLowerCase();
        const txMatch = selectedTransmission === "All" || v.transmission.toLowerCase() === selectedTransmission.toLowerCase();
        return fuelMatch && txMatch;
    });

    const visible = showAll ? filtered : filtered.slice(0, DEFAULT_VISIBLE);
    const isEmpty = filtered.length === 0;

    // ── Compare helpers ────────────────────────────────────────────────────────

    const isSelected = (id: string) => compareList.some((v) => v.id === id);

    const toggleCompare = (variant: VariantItem) => {
        if (isSelected(variant.id)) {
            setCompareList((prev) => prev.filter((v) => v.id !== variant.id));
        } else if (compareList.length < MAX_COMPARE) {
            setCompareList((prev) => [...prev, variant]);
        }
    };

    const removeFromCompare = (id: string) => {
        setCompareList((prev) => prev.filter((v) => v.id !== id));
        if (compareList.length === 1) setSheetOpen(false);
    };

    const handleSelectorSelect = useCallback(
        (_index: number, data: SelectedCar) => {
            if (compareList.some((v) => v.id === data._id) || compareList.length >= MAX_COMPARE) return;
            setCompareList((prev) => [...prev, selectedCarToVariantItem(data)]);
            setShowCompareSelector(false);
        },
        [compareList],
    );

    const handleCompare = useCallback(() => {
        if (compareList.length < 2) return;
        const keys = compareList.map(toCompareKey).filter(Boolean);
        Cookies.set("selected_variant_slugs", JSON.stringify(keys), { path: "/" });
        const detailedSlugs = compareList.map((v) => v.modelDetailedSlug).filter(Boolean);
        router.push(`/compare-cars/${generateCompareURLByDetailedSlugs(detailedSlugs)}`);
    }, [compareList, router]);

    const currentMonth = new Intl.DateTimeFormat("en-US", {
        month: "long",
    }).format(new Date());
    // const viewOfferCTAText = `View ${currentMonth} Offers`;
    const viewOfferCTAText = `View Diwali Offers`;

    // lead buttons based on variant status
    const renderLeadButton = (variant: any, className: string) => (
        <>
            {variant?.isDiscontinued ? (
                <button disabled className={`${className} ${discVairentBtnClass}`}>
                    Discontinued
                </button>
            ) : variant?.isUpcoming ? (
                <LeadButton
                    carId={variant.modelId}
                    variantId={variant?.id}
                    carName={`${variant.brandName} ${variant.modelName} ${variant.name}`}
                    btnText="Notify Me"
                    className={className}
                    is_upcoming={variant.isUpcoming}
                    datatestid="upcoming-lead"
                />
            ) : (
                <LeadButton
                    carId={variant.modelId}
                    variantId={variant?.id}
                    carName={`${variant.brandName} ${variant.modelName} ${variant.name}`}
                    btnText={<span className="flex items-center justify-center gap-2 cursor-pointer">{viewOfferCTAText}</span>}
                    className={className}
                    is_upcoming={variant.isUpcoming}
                    datatestid="offer-lead"
                />
            )}
        </>
    );

    // fuelTabs based on variants prop
    let fuelTabs = ["All", ...new Set(variants.map((v) => v.fuel).filter(Boolean))];
    if (fuelTabs.length <= 2) {
        fuelTabs = [];
    }

    // transmissionTabs based on variants prop
    let transmissionTabs = useMemo(() => {
        const filteredByFuel = selectedFuel === "All" ? variants : variants.filter((v) => v.fuel.toLowerCase() === selectedFuel.toLowerCase());
        const transmission = [...new Set(filteredByFuel.map((v) => v.transmission).filter(Boolean))].sort((a: any, b: any) => b.localeCompare(a))
        return ["All", ...transmission];
    }, [variants, selectedFuel]);

    if (transmissionTabs.length <= 2) {
        transmissionTabs = [];
    }

    useEffect(() => {
        if (transmissionTabs.length === 0) {
            setSelectedTransmission("All");
        }
    }, [transmissionTabs]);

    const upcomingCount = variants?.filter((v: any) => v.isUpcoming).length;
    const nonUpcomingCount = variants.length - upcomingCount;
    const showCompareColumn = nonUpcomingCount > 0;

    const isRecentlyLaunched = (variant: VariantItem) => {
        return variant?.status === 'new-launched';
    };

    return (
        <>
            <div
                id="variants-table-top"
                className="shadow-none md:shadow-[0px_2px_12px_0px_#00000014] rounded-0 md:rounded-3xl border-0 md:border border-gray-200 bg-white p-4 md:p-6 -mx-[18px] md:mx-0"
            >
                {/* Header */}

                <Heading tag="h2" className="!text-[22px] font-bold text-[#161A22] !md:text-[26px]">
                    {title}
                </Heading>
                <SectionDescription text={subtitle} className="mt-1" />

                {/* ── Fuel Type Tabs ── */}
                {fuelTabs.length > 0 && (
                    <div className="mt-5 flex gap-3 overflow-x-auto pb-1">
                        {fuelTabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setSelectedFuel(tab)}
                                className={`relative whitespace-nowrap px-4 py-2 text-sm font-medium transition-all ${
                                    selectedFuel === tab
                                        ? "text-teal-700 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-teal-700"
                                        : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Transmission Tabs ── */}
                {transmissionTabs.length > 0 && (
                    <div className="mt-3 flex gap-2 border border-gray-200 rounded-full bg-[#F9F9F9] p-2 inline-flex">
                        {transmissionTabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setSelectedTransmission(tab)}
                                className={`rounded-full px-4 py-1 text-sm font-medium transition-colors ${selectedTransmission === tab ? "bg-teal-700 text-white" : ""}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Desktop Table ── */}
                <div className="mt-6 hidden md:block">
                    {isEmpty ? (
                        <div className="text-center py-10">
                            <p className="text-gray-600 text-md">We couldn't find any variants matching your selection</p>
                        </div>
                    ) : (
                        <>
                            {/* Table Header */}
                            <div className="grid grid-cols-12 bg-gray-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 rounded-tl-[16px] rounded-tr-[16px]">
                                <div className="col-span-5">Variant</div>
                                <div className="col-span-3">Ex-Showroom Price</div>
                                <div className="col-span-2 text-center">Offers</div>
                                {showCompareColumn && 
                                    <div className="col-span-2 text-right">Compare</div>
                                }
                            </div>

                            {/* Table Rows */}
                            {visible.map((variant) => (
                                <div key={variant.id} className="grid grid-cols-12 items-center px-4 py-4">
                                    {/* Variant Info */}
                                    <div className="col-span-5">
                                        {isRecentlyLaunched(variant) && (
                                            <span className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wide text-[#fff] bg-[#FD9C00] border border-[#FD9C00] rounded-[6px] mb-1 leading-[10px]">Recently Launched</span>
                                        )}
                                        {!variant?.is_sanity_done || !variant?.slug ? (
                                            <p className="text-[14px] font-semibold text-gray-900">
                                                {variant.modelName} {variant.name}
                                            </p>
                                        ) : (
                                            <Link prefetch={false} title={`${variant.modelName} ${variant.name}`} href={`/${variant.brandSlug}/${variant.modelSlug}/${variant.slug}`}>
                                                <p className="text-[14px] font-semibold text-gray-900">
                                                    {variant.modelName} {variant.name}
                                                </p>
                                            </Link>
                                        )}
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {[variant.engine, variant.transmission, variant.fuel].filter(Boolean).map((tag) => (
                                                <Chip key={tag}>{tag}</Chip>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className="col-span-3 text-[15px] font-bold text-gray-900">{variant.price}</div>

                                    {/* Offers */}
                                    <div className="col-span-3 flex justify-center">
                                        {renderLeadButton(
                                            variant,
                                            "cursor-pointer w-full rounded-[12px] border border-[#008C8B] px-4 py-[12px] text-[14px] font-[700] text-[#008C8B] hover:border-[#008C8B]",
                                        )}
                                    </div>

                                    {/* Compare Checkbox */}
                                    <div className="col-span-1 flex justify-end">
                                        {/* Hide compare checkbox for upcoming cars */}
                                        {!variant.isUpcoming && (
                                            <CompareCheckbox
                                                checked={isSelected(variant.id)}
                                                disabled={!isSelected(variant.id) && compareList.length >= MAX_COMPARE}
                                                onChange={() => toggleCompare(variant)}
                                                variantName={`${variant.modelName} ${variant.name}`}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* ── Mobile Cards ── */}
                <div className="mt-4 md:hidden">
                    {isEmpty ? (
                        <div className="text-center py-6">
                            <p className="text-gray-600 text-sm">We couldn't find any variants matching your selection</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile table header */}
                            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 -mx-3">
                                <div>Variant</div>
                                <div>Ex-Showroom Price</div>
                            </div>

                            {visible.map((variant) => (
                                <div key={variant.id} className="border-b border-gray-200 p-4 -mx-3">
                                    {/* Top row */}
                                    <div className="flex w-full items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            {!variant?.is_sanity_done || !variant?.slug ? (
                                                <p className="text-[14px] font-semibold text-gray-900">
                                                    {variant.modelName} {variant.name}
                                                </p>
                                            ) : (
                                                <Link
                                                    prefetch={false}
                                                    title={`${variant.modelName} ${variant.name}`}
                                                    href={`/${variant.brandSlug}/${variant.modelSlug}/${variant.slug}`}
                                                >
                                                    <p className="text-[14px] font-semibold text-gray-900">
                                                        {variant.modelName} {variant.name}
                                                    </p>
                                                </Link>
                                            )}

                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {[variant.engine, variant.transmission, variant.fuel]
                                                    .filter(Boolean)
                                                    .map((tag) => (
                                                        <Chip key={tag}>{tag}</Chip>
                                                    ))}
                                            </div>
                                        </div>

                                        <div className="ml-auto shrink-0 self-start border border-gray-200 rounded-md p-2 text-[15px] font-bold text-gray-900 text-right whitespace-nowrap">
                                            {variant.price}
                                        </div>
                                    </div>

                                    {/* Offers button */}
                                    <div className="flex items-center justify-between gap-2 mt-3 md-mt-0">
                                        {renderLeadButton(variant, "mt-3 rounded-[12px] border border-[#008C8B] py-2 text-[13px] font-medium text-[#008C8B] w-[190px]")}

                                        {!variant.isUpcoming && (
                                            <CompareCheckbox
                                                checked={isSelected(variant.id)}
                                                disabled={!isSelected(variant.id) && compareList.length >= MAX_COMPARE}
                                                onChange={() => toggleCompare(variant)}
                                                label="COMPARE"
                                                variantName={`${variant.modelName} ${variant.name}`}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* ── Load More ── */}
                {filtered.length > DEFAULT_VISIBLE && (
                    <button
                        onClick={() => setShowAll((prev) => !prev)}
                        className="flex w-[calc(100%+1.5rem)] md:w-full items-center justify-center gap-1.5 text-[14px] font-semibold text-teal-700 hover:text-teal-800 bg-[#F9F9F9] py-4 cursor-pointer -mx-3 md:mx-0 md:rounded-bl-[16px] md:rounded-br-[16px]"
                    >
                        {showAll ? "Show Less Variants" : "Load More Variants"}
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                            alt={title}
                            title={title}
                            width={20}
                            height={20}
                            className={`h-4 w-4 transition-transform ${showAll ? "rotate-270" : "rotate-90"} [filter:brightness(0)_saturate(100%)_invert(39%)_sepia(77%)_saturate(876%)_hue-rotate(137deg)_brightness(93%)_contrast(101%)]`}
                        />
                    </button>
                )}
            </div>

            {/* ── Sticky Compare Button ── */}
            {compareList.length > 0 && !sheetOpen && (
                <div className="fixed bottom-[100px] md:bottom-6 left-1/2 z-40 -translate-x-1/2">
                    <button
                        onClick={() => setSheetOpen(true)}
                        className="flex items-center gap-2.5 rounded-full bg-teal-700 px-6 py-3 text-[14px] font-semibold text-white shadow-xl shadow-teal-900/30 transition hover:bg-teal-800 cursor-pointer"
                    >
                        Compare Cars
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[13px] font-bold text-teal-700">{compareList.length}</span>
                    </button>
                </div>
            )}

            {/* ── Bottom Sheet Backdrop ── */}

            {/* ── Bottom Sheet ── */}
            <div
                className={`fixed bottom-0 left-0 right-0 z-50 bg-[#F9F9F9] transition-transform duration-300 ease-out shadow-none md:shadow-[0px_2px_12px_0px_#00000040] ${
                    sheetOpen ? "translate-y-0" : "translate-y-full"
                }`}
            >
                <Container>
                    {/* Sheet Header */}
                    <div className="flex items-center justify-between px-0 py-4">
                        <p className="text-[17px] font-bold text-gray-900">My Comparison</p>
                        <button onClick={() => setSheetOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100" aria-label="Close">
                            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Sheet Content */}
                    <div className="max-h-[60vh] overflow-y-auto px-0">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {compareList.map((car) => (
                                <div key={car.id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-4 items-start">
                                    {/* Car Image */}
                                    <div className="h-[64px] w-[64px] flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                        <ImageWithFallback
                                            src={car?.featured_image ? `${process.env.NEXT_PUBLIC_CDN_URL}${car.featured_image}` : undefined}
                                            alt={car?.name}
                                            width={64}
                                            height={64}
                                            className="h-full w-full object-cover"
                                        />
                                    </div>

                                    {/* Car Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[13px] font-semibold text-gray-900 truncate">{car.name}</p>
                                        <p className="mt-0.5 text-[12px] text-gray-500">
                                            {car.fuel}
                                            {car.mileage ? `, ${car.mileage}` : ""}
                                            {car.transmission ? `, ${car.transmission}` : ""}
                                        </p>
                                        <p className="mt-1 text-[13px] font-bold text-gray-800">{car.price}</p>
                                    </div>

                                    {/* Remove button */}
                                    <button
                                        onClick={() => removeFromCompare(car.id)}
                                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                                        aria-label={`Remove ${car.name}`}
                                    >
                                        <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}

                            {/* Add More slot */}
                            {compareList.length < MAX_COMPARE && (
                                <button
                                    onClick={() => {
                                        setShowCompareSelector(true);
                                    }}
                                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 py-4 text-[13px] font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer"
                                >
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add More
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Sheet Footer CTA */}
                    <div className="border-t border-gray-100 px-0 pb-8 pt-4">
                        <button
                            onClick={handleCompare}
                            className="flex items-center gap-2.5 rounded-full bg-teal-700 px-6 py-3 text-[14px] font-semibold text-white shadow-xl shadow-teal-900/30 transition hover:bg-teal-800 mx-auto cursor-pointer"
                        >
                            Compare Cars{" "}
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[13px] font-bold text-teal-700">{compareList.length}</span>
                        </button>
                    </div>
                </Container>
            </div>

            {showCompareSelector && (
                <CarSelector
                    index={compareList.length}
                    brands={brands}
                    brandsLoading={brandsLoading}
                    models={selectorModels}
                    variants={selectorVariants}
                    setModels={setSelectorModels}
                    setVariants={setSelectorVariants}
                    onSelect={handleSelectorSelect}
                    onClose={() => setShowCompareSelector(false)}
                    selectedVariantIds={compareList.map((v) => v.id)}
                />
            )}
        </>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Chip({ children }: { children: React.ReactNode }) {
    return <span className="rounded-[8px] bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">{children}</span>;
}

function CompareCheckbox({ checked, disabled, onChange, label, variantName }: { checked: boolean; disabled: boolean; onChange: () => void; label?: string; variantName: string }) {
    return (
        <div className="flex flex-row items-center gap-1">
            {label && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>}
            <button
                onClick={onChange}
                disabled={disabled}
                aria-pressed={checked}
                aria-label={`${checked ? "Remove" : "Add"} ${variantName} ${checked ? "from" : "to"} compare`}
                className={`flex h-7 w-7 items-center justify-center rounded-md border-2 transition-colors cursor-pointer ${
                    checked
                        ? "border-teal-700 bg-teal-700"
                        : disabled
                          ? "!cursor-not-allowed border-gray-200 bg-gray-50 opacity-40"
                          : "border-gray-300 bg-white hover:border-teal-500"
                }`}
            >
                {checked && (
                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </button>
        </div>
    );
}
